/**
 * Audience-landing intake chat (modal).
 *
 * Hosts the /family-offices and /for-funds landing experience (embedded
 * in the hero). A Claude-driven conversation captures name /
 * organization / website / email / role / use-case, then a
 * deterministic tier rule classifies the visitor:
 *
 *   A -> auto-qualified; the chat captured a preferred time for a
 *        15-minute discovery call and the team sends the invite
 *   B -> human-review queue; "we'll be in touch within 24h"
 *   C -> soft close; review queue (visitor never sees a rejection)
 *
 * Backend endpoints:
 *   POST /public/intake/start   -- create session, return greeting
 *   POST /public/intake/message -- visitor turn
 *
 * UTM context is forwarded from the landing page so attribution flows
 * into the leads table via the backend.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface IntakeResponse {
  session_id: string;
  reply: string;
  turn_count: number;
  completed?: boolean;
  final_tier?: 'A' | 'B' | 'C' | null;
  tier_reasoning?: string | null;
}

const SESSION_KEY = 'vos_intake_session_id';

const IDLE_NUDGE_1 = "Still with me? Happy to keep going whenever you're ready.";
const IDLE_NUDGE_2 =
  "No worries -- looks like you stepped away. The conversation is saved; pop back any time and we'll pick up where we left off.";

function getOrCreateSessionId(): string {
  // sessionStorage can throw (Safari private mode, storage-restricted
  // webviews) — degrade to an in-memory id rather than crashing the
  // chat before it starts.
  let id: string | null = null;
  try {
    id = sessionStorage.getItem(SESSION_KEY);
  } catch { /* ignore */ }
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    try {
      sessionStorage.setItem(SESSION_KEY, id);
    } catch { /* ignore */ }
  }
  return id;
}

interface Props {
  /** Whether the chat is rendered. For `mode='modal'` this gates the
   *  overlay; for `mode='inline'` it gates whether the chat session
   *  is started (lets the parent control eager-start vs lazy-start). */
  open: boolean;
  /** Modal-only — called when the visitor closes the overlay. */
  onClose?: () => void;
  /** 'modal' renders an overlay above the page (legacy behaviour
   *  retained for CTAs that still want it). 'inline' renders the
   *  chat as a card-sized block in the page layout so the chat can
   *  *host* the page (visible without a click). */
  mode?: 'modal' | 'inline';
  audience: string;
  audienceLabel: string;
  utm: UtmParams;
}

const AudienceIntakeChat: React.FC<Props> = ({
  open, onClose, mode = 'modal', audience, audienceLabel, utm,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [finalTier, setFinalTier] = useState<'A' | 'B' | 'C' | null>(null);
  const [started, setStarted] = useState(false);
  const [startFailed, setStartFailed] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sessionIdRef = useRef<string>('');
  // Guards against the start effect double-firing (re-renders, prop
  // identity changes). Two concurrent /start POSTs with the same
  // session_id race on the unique-key insert — on slow (mobile)
  // connections the loser surfaced as "Could not start the chat."
  const startInFlightRef = useRef(false);
  const utmRef = useRef(utm);
  utmRef.current = utm;

  // Reset when the modal closes so the next open starts cleanly. We
  // keep the session_id in sessionStorage so a mid-conversation reload
  // can be recovered, but clear UI state on explicit close.
  useEffect(() => {
    if (!open) {
      setStarted(false);
      setStartFailed(false);
      startInFlightRef.current = false;
      return;
    }
    // startFailed parks the effect until the visitor taps "Try again"
    // (which clears it) — otherwise a persistent failure would loop.
    if (started || startFailed || startInFlightRef.current) return;
    startInFlightRef.current = true;
    sessionIdRef.current = getOrCreateSessionId();
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.post<IntakeResponse>('/public/intake/start', {
          session_id: sessionIdRef.current,
          audience,
          ...utmRef.current,
        });
        if (cancelled) return;
        const data = resp.data;
        setMessages([{ role: 'assistant', content: data.reply }]);
        setCompleted(!!data.completed);
        setFinalTier(data.final_tier || null);
        setStarted(true);
      } catch (err: any) {
        startInFlightRef.current = false;
        if (cancelled) return;
        setStartFailed(true);
        toast.error(err?.response?.data?.detail || 'Could not start the chat.');
      }
    })();
    return () => { cancelled = true; };
    // utm intentionally read via ref — its identity must not re-fire
    // the start sequence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, audience, started, startFailed]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Idle nudge: when the visitor goes quiet after Claude's last
  // message, send a soft "Still with me?" client-side (not via the
  // backend -- these aren't part of the conversation history Claude
  // sees, just UX prompts). A second prompt fires after another
  // interval offering a graceful exit. Both clear as soon as the
  // visitor types or sends.
  const NUDGE_1_MS = 30_000;
  const NUDGE_2_MS = 75_000;
  const hasUserActivity = sending || input.trim().length > 0;
  useEffect(() => {
    if (completed || !started || hasUserActivity) return;
    if (messages.length === 0) return;
    // Never nudge a visitor who hasn't engaged yet — the greeting on
    // page load isn't a conversation. Nudges only make sense once
    // they've replied at least once and then gone quiet.
    if (!messages.some((m) => m.role === 'user')) return;
    const last = messages[messages.length - 1];
    if (last.role !== 'assistant') return;
    // Don't re-nudge if the last message IS itself one of our nudges.
    if (last.content === IDLE_NUDGE_1 || last.content === IDLE_NUDGE_2) return;

    const t1 = window.setTimeout(() => {
      setMessages((m) => [...m, { role: 'assistant', content: IDLE_NUDGE_1 }]);
    }, NUDGE_1_MS);
    const t2 = window.setTimeout(() => {
      setMessages((m) => [...m, { role: 'assistant', content: IDLE_NUDGE_2 }]);
    }, NUDGE_1_MS + NUDGE_2_MS);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, [messages, completed, started, hasUserActivity]);

  const sendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending || completed) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: trimmed }]);
    setSending(true);
    try {
      const resp = await api.post<IntakeResponse>('/public/intake/message', {
        session_id: sessionIdRef.current,
        message: trimmed,
      });
      const data = resp.data;
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
      if (data.completed) {
        setCompleted(true);
        setFinalTier(data.final_tier || null);
        // The session belongs to this completed run; a fresh chat
        // should get a fresh session_id on the next open.
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Message failed. Try again.';
      toast.error(detail);
      // Roll the user message back so they can retry.
      setMessages((m) => m.slice(0, -1));
      setInput(trimmed);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const panel = (
    <div
      className={
        mode === 'modal'
          ? 'relative flex w-full max-w-lg flex-col rounded-3xl bg-white shadow-card'
          : 'relative flex w-full flex-col rounded-3xl bg-white shadow-card border border-apple-divider'
      }
      style={mode === 'modal' ? { height: 'min(640px, 92vh)' } : { height: 'min(560px, 75vh)' }}
      onClick={mode === 'modal' ? (e) => e.stopPropagation() : undefined}
    >
      {mode === 'modal' && (
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 text-apple-subtext hover:text-apple-ink"
        >
          <X className="h-5 w-5" />
        </button>
      )}

      <header className="border-b border-apple-divider px-6 py-4">
        <h3 className="text-base font-semibold text-apple-ink">
          VentureOS intake
        </h3>
        <p className="mt-0.5 text-xs text-apple-subtext">
          ~60 seconds. Built for {audienceLabel}.
        </p>
      </header>

        {/* Chat transcript */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!started && !startFailed && (
            <div className="flex items-center gap-2 text-sm text-apple-subtext">
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting…
            </div>
          )}
          {!started && startFailed && (
            <div className="flex flex-col items-start gap-3 text-sm text-apple-subtext">
              <span>The chat couldn't connect.</span>
              <button
                onClick={() => setStartFailed(false)}
                className="inline-flex items-center justify-center rounded-full bg-apple-blue px-4 py-2 text-sm font-medium text-white hover:bg-apple-blueHover"
              >
                Try again
              </button>
            </div>
          )}
          {messages.map((m, i) => (
            <MessageBubble key={i} role={m.role} content={m.content} />
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-xs text-apple-subtext">
              <Loader2 className="h-3 w-3 animate-spin" />
              Thinking…
            </div>
          )}
          {completed && finalTier && (
            <CompletionPanel tier={finalTier} onClose={onClose} />
          )}
        </div>

      {/* Composer */}
      {!completed && (
        <form
          onSubmit={sendMessage}
          className="border-t border-apple-divider px-4 py-3"
        >
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              rows={1}
              placeholder="Type your reply…"
              disabled={sending || !started}
              className="flex-1 resize-none rounded-2xl border border-apple-divider px-4 py-2.5 text-sm focus:border-apple-blue focus:outline-none disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={sending || !started || !input.trim()}
              className="inline-flex items-center justify-center rounded-full bg-apple-blue p-2.5 text-white transition-colors hover:bg-apple-blueHover disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </form>
      )}
    </div>
  );

  if (mode === 'inline') return panel;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      {panel}
    </div>
  );
};

const MessageBubble: React.FC<{ role: 'user' | 'assistant'; content: string }> = ({ role, content }) => {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? 'bg-apple-blue text-white'
            : 'bg-apple-bg text-apple-ink border border-apple-divider'
        }`}
      >
        {content}
      </div>
    </div>
  );
};

const CompletionPanel: React.FC<{
  tier: 'A' | 'B' | 'C';
  /** Optional -- inline-mode chats have no overlay to close, so the
   *  B / C terminal panels just omit the Close button when this is
   *  absent. */
  onClose?: () => void;
}> = ({ tier, onClose }) => {
  if (tier === 'A') {
    return (
      <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-5">
        <CheckCircle2 className="h-7 w-7 text-green-600" />
        <h4 className="mt-3 text-base font-semibold text-green-900">
          You're in.
        </h4>
        <p className="mt-1 text-sm text-green-900/80">
          You're a strong fit — keep an eye on your inbox for a calendar
          invite for your 15-minute discovery call.
        </p>
      </div>
    );
  }
  if (tier === 'B') {
    return (
      <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-5">
        <h4 className="text-base font-semibold text-blue-900">
          Thanks — we've got it.
        </h4>
        <p className="mt-2 text-sm text-blue-900/80">
          A member of the team will review your details and follow up within
          24 hours with next steps.
        </p>
        {onClose && (
          <button
            onClick={onClose}
            className="mt-4 inline-flex items-center justify-center rounded-full border border-blue-300 bg-white px-5 py-2.5 text-sm font-medium text-blue-900 hover:bg-blue-100"
          >
            Close
          </button>
        )}
      </div>
    );
  }
  // Tier C — soft landing. Internally this is a weak-signal lead, but
  // the visitor is never told "not a fit"; a human reviews and decides.
  return (
    <div className="mt-4 rounded-2xl border border-apple-divider bg-apple-bg p-5">
      <h4 className="text-base font-semibold text-apple-ink">
        Thanks for reaching out.
      </h4>
      <p className="mt-2 text-sm text-apple-subtext">
        We've got everything we need for now — someone from the team
        will reach out to you soon.
      </p>
      {onClose && (
        <button
          onClick={onClose}
          className="mt-4 inline-flex items-center justify-center rounded-full border border-apple-divider bg-white px-5 py-2.5 text-sm font-medium text-apple-ink hover:bg-apple-bg"
        >
          Close
        </button>
      )}
    </div>
  );
};

export default AudienceIntakeChat;
